// ═══════════════════════════════════════════════════════════════════════════
// brandsAdmin.js — the admin-side brand review queue.
//
// Brands post deals immediately, but their deals stay hidden from hosts until
// a human approves the brand (RLS gate, migration 075). This is the vetting
// layer: list brand accounts, see their deals + contact + advisory risk flags,
// and approve / reject / suspend. All gated by has_cms_access server-side.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { logAdminAction } from './audit';

// Cheap, advisory content scan surfaced to the reviewer — NEVER an auto-gate.
// A hit just means "look closer," it doesn't block or approve anything.
const RISK_PATTERNS = [
  /guaranteed?\s+(income|returns|money|profit)/i,
  /\b(crypto|nft|forex|ico|token)\b/i,
  /get\s+rich|make\s+\$?\d+\s*(k|,000)?\s*(a|per)\s+(day|week)/i,
  /\b(cbd|kratom|delta[-\s]?8|thc)\b/i,
  /\b(gambling|casino|betting|sportsbook)\b/i,
  /\b(weight\s*loss|miracle|detox\s+tea)\b/i,
  /\b(adult|xxx|escort)\b/i,
  /\b(mlm|pyramid|downline)\b/i,
];

/** Advisory risk flags for a brand, derived from its deals' text + contact.
 *  Pure — returns a list of short human-readable flags (empty = looks clean). */
export function brandRiskFlags(brand) {
  const flags = [];
  const email = (brand?.contact_email || '').trim();
  if (!email) flags.push('No contact email');
  else if (/@(gmail|yahoo|hotmail|outlook|icloud|proton)\./i.test(email)) flags.push('Free-provider email');
  if (!(brand?.contact_name || '').trim()) flags.push('No point of contact');

  const text = (brand?.deals || [])
    .map((d) => `${d.title || ''} ${d.tagline || ''}`)
    .join(' ');
  for (const re of RISK_PATTERNS) {
    if (re.test(text)) { flags.push('Risky deal content'); break; }
  }
  return flags;
}

/** List brand accounts for the review queue, optionally by status. */
export async function listBrands(status = null) {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('admin_list_brands', { p_status: status });
  if (error || data?.error) {
    console.error('[brandsAdmin] listBrands:', error?.message || data?.error);
    return [];
  }
  return data.brands || [];
}

/** Approve / reject / suspend / re-queue a brand. Logged to the audit trail. */
export async function setBrandStatus(userId, status) {
  if (!supabase) throw new Error('Offline.');
  const { data, error } = await supabase.rpc('admin_set_brand_status', {
    p_user_id: userId,
    p_status: status,
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  logAdminAction('brand_status', { targetType: 'brand', targetId: userId, detail: { status } });
  return true;
}
